import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  SimpleChanges,
} from "@angular/core";
import { ApiService } from "src/app/services/api.service";
import { removeTags, searchMatchContext } from "src/app/utils/string-transform";
import { environment } from "src/environments/environment";
import { SearchTermInterface } from "src/app/types/search";

/**
 * Typeahead for search modal with context highlighting
 */
@Component({
  host: {
    "(document:click)": "closeAutocomplete($event)",
  },
  selector: "app-typeahead",
  templateUrl: "./typeahead.component.html",
  styleUrls: ["./typeahead.component.scss"],
})
export class TypeaheadComponent {
  @Input() inputValue: string = "";
  @Output() userSelectedResult: EventEmitter<object>;

  @ViewChild("filterList") public filterList: ElementRef;

  xhrInProgress: boolean = false;
  submittedSearch: boolean = false;
  minimumCharacters: number = 2;
  maximumCharacters: number = 40;
  debounceTime: number = 700;
  searchPhrase: string;
  searchObject: SearchTermInterface;
  resultDataList: SearchTermInterface[];

  constructor(private API: ApiService) {
    this.userSelectedResult = new EventEmitter<object>();
  }

  /**
   * Set search phrase on input value change
   */
  ngOnChanges(changes: SimpleChanges) {
    this.searchPhrase = changes.inputValue.currentValue;
  }

  /**
   * Feth results from API according to phrase
   */
  searchQueryOnDataSource($event): void {
    if (
      !this.xhrInProgress &&
      $event.target.value.length >= this.minimumCharacters &&
      $event.target.value.toLowerCase() !==
        this.searchObject?.originalText.toLowerCase() &&
      $event.target.value.toLowerCase() !== this.searchPhrase?.toLowerCase()
    ) {
      this.xhrInProgress = true;

      this.API.GET(
        `search/${environment.mall_id}/stores/auto?q=${$event.target.value}`
      ).subscribe({
        next: (response) => {
          this.doQuerySearch(
            response.results[0]
              ? response.results.map((item) => Object.assign({}, item))
              : [],
            $event.target.value
          );
        },
        error: () => {
          this.xhrInProgress = false;
        },
      });
    }
  }

  /**
   * Sanitize and highlight context match within search message
   */
  private doQuerySearch(dataArray: Array<any>, queryString: string): void {
    if (!this.submittedSearch) {
      this.resultDataList = [];

      // Initial list of results
      for (const element of dataArray) {
        this.resultDataList.push(element);
      }

      // No results found
      if (this.resultDataList.length === 0) {
        const element = {
          title:
            "We couldn't find any store matching the phrase " +
            queryString +
            ".",
        };

        this.resultDataList.push(element);
      }

      // Transform result & highlight matched string
      for (const element of this.resultDataList) {
        const regex = new RegExp("(" + queryString + ")", "gi");
        let updatedText: string;
        if (this.resultDataList.length > 0) {
          updatedText = element.title.replace(
            regex,
            "<strong>" + queryString + "</strong>"
          );
        }
        element.originalText = element.title;
        element.title = updatedText;
        element.match = searchMatchContext(element);
      }

      this.xhrInProgress = false;
    }
  }

  /**
   * Track by function for results
   */
  trackBySlug(index: number, el: any): number {
    return el.slug;
  }

  /**
   * On input focus, set value to searched prase
   */
  focusInput() {
    if (
      this.searchObject &&
      this.searchObject.term.includes(this.searchPhrase)
    ) {
      this.inputValue = this.searchObject.originalText;
      this.searchPhrase = this.inputValue;
    } else {
      this.searchObject = null;
    }
  }

  /**
   * On inpur blur, rest to matched phrase with context
   */
  blurInput() {
    if (
      this.inputValue === this.searchObject?.originalText &&
      this.searchObject?.match
    ) {
      this.inputValue = removeTags(this.searchObject.match);
      this.searchPhrase = this.inputValue;
    }
  }

  /**
   * On input enter, search stores with free text
   */
  enterInput() {
    this.xhrInProgress = true;
    this.submittedSearch = true;

    setTimeout(() => {
      let query = {
        originalText: this.inputValue,
        title: this.inputValue,
        match: this.inputValue,
        type: "store",
        freeText: true,
      };
      this.selectedResult(query);
    }, this.debounceTime);
  }

  /**
   * Find results for selected autocomplete suggestion
   */
  selectedResult(result: any): void {
    if (result.type) {
      this.searchObject = result;
      this.searchPhrase = removeTags(result.match);
      this.inputValue = this.searchPhrase;
      this.resultDataList = [];
      this.userSelectedResult.emit({
        target: result || null,
      });

      this.xhrInProgress = false;
      this.submittedSearch = false;
    }
  }

  /**
   * Close autocomplete suggestion
   */
  closeAutocomplete(event = undefined): void {
    if (!this.filterList.nativeElement.contains(event.target)) {
      this.resultDataList = [];
      this.inputValue = this.searchPhrase;
    }
  }

  /**
   * Clear search
   */
  clearSearch(): void {
    this.inputValue = null;
    this.searchPhrase = null;
    this.searchObject = null;
    this.resultDataList = [];
    this.userSelectedResult.emit({
      target: null,
    });
  }
}
